import React, { useState, useContext, useEffect } from "react";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import { ReactstrapInput } from "reactstrap-formik";
import * as Yup from "yup";
import { Row, Col, Button, Card, CardHeader, CardBody, CardTitle } from "reactstrap";
import { useParams, useHistory } from "react-router-dom";
import SettingsManagementService from "core/api/SettingsManagementService";
import { SETTING_DATA_TYPE_OPTIONS, parseValue, stringifyValue } from "./SettingFunctions";
import { UserContext } from "context/UserContext";
import PanelHeader from "components/PanelHeader/PanelHeader.jsx";


const SettingSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  displayName: Yup.string().required("Required"),
  // dataType: Yup.string().required("Data type is required"),
  value: Yup.mixed().test("validate-value", function (value) {
    const { dataType } = this.parent;
    // Dynamic error messages
    const errorMessages = {
      int: "Value must be an integer",
      double: "Value must be a number",
      bool: "Value must be Yes or No",
      json: "Value must be a valid JSON",
    };

    if (dataType === "int" || dataType === "double") {
      if (typeof value === "boolean" || value === null || value === undefined) {
        return this.createError({ message: errorMessages[dataType] });
      }
      if (typeof value === "string" && value.trim() === "") {
        return this.createError({ message: errorMessages[dataType] });
      }

      if (dataType === "int" && !Number.isInteger(Number(value))) {
        return this.createError({ message: errorMessages.int });
      }
      if (dataType === "double" && isNaN(Number(value))) {
        return this.createError({ message: errorMessages.double });
      }
    }

    if (dataType === "bool") {
      if (value !== "true" && value !== "false" & value !== true && value !== false) {
        return this.createError({ message: errorMessages.bool });
      }
    }
    if (dataType === "json") {
      try {
        JSON.parse(value);
      } catch {
        return this.createError({ message: errorMessages.json });
      }
    }
    return true; // If all validations pass
  }),
});



const Edit = () => {
  let { id } = useParams();
  const history = useHistory();
  const { userHasAnyPermissions } = useContext(UserContext)
  const [loading, setLoading] = useState(true)

  const isUpdate = id != null;

  const initialData = {
    id: null,
    name: "",
    displayName: "",
    value: "",
    dataType: "string",
    notes: "",
  };

  const [data, setData] = useState(initialData)


  const submitForm = async (values, { setSubmitting }) => {
    const newData = { ...values, value: parseValue(values.value, values.dataType) };
    const res = await SettingsManagementService.add(newData);
    setData(res);

    if (!isUpdate) {
      history.push(`${res.id}`);
    } else {
      history.push(`../${res.id}`);
    }

    setSubmitting(false);
  }

  const cancel = () => {
    history.goBack()
  }

  useEffect(() => {
    async function fetchData() {
      var setting = await SettingsManagementService.get(id);
      setData(setting);
      setLoading(false);
    }
    if (id)
      fetchData()
    else
      setLoading(false);
  }, [id]);


  if (loading)
    return <>loading</>;

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Setting - {data.name}</CardTitle>
              </CardHeader>
              <CardBody>
                <Formik
                  initialValues={data}
                  validationSchema={SettingSchema}
                  onSubmit={submitForm}>
                  {({ isSubmitting }) => (
                    <Form className="form-horizontal">

                      <Row>
                        {/*  Only visibile to someone who has explicit instuctions to edit this */}
                        {userHasAnyPermissions(["write:settingsManagement"]) &&
                          <Col md="3">
                            <Field
                              type="text"
                              name="name"
                              label="Setting Name"
                              component={ReactstrapInput}
                            />
                          </Col>
                        }

                        <Col md="3">
                          <Field
                            type="text"
                            name="displayName"
                            label="Display Name"
                            component={ReactstrapInput}
                          />
                        </Col>
                      </Row>

                      <Row>
                        {/*  Only visibile to someone who has explicit instuctions to edit this */}
                        {userHasAnyPermissions(["write:settingsManagement"]) &&
                          <Col md="3">
                            <div className="form-group">
                              <div>
                                <label>Data Type</label>
                              </div>
                              <Field as="select" name="dataType" className="form-control">

                                {SETTING_DATA_TYPE_OPTIONS.map(t => (
                                  <option key={t.value} value={t.value}>
                                    {t.label}
                                  </option>
                                ))}
                              </Field>
                            </div>
                          </Col>
                        }

                        <Col md="3">
                          <Field name="value">
                            {({ field, form }) => {
                              const renderFieldComponent = (dataType) => {
                                switch (dataType) {
                                  case "bool":
                                    return (
                                      <select {...field} className="form-control">
                                        <option value="">Select</option>
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                      </select>
                                    );
                                  case "json":
                                    return <textarea {...field} rows={5} className="form-control" />;
                                  default:
                                    return <input {...field} type="text" className="form-control" />;
                                }
                              };

                              return (
                                <div className="form-group">
                                  <div>
                                    <label >Value</label>
                                  </div>
                                  {renderFieldComponent(form.values.dataType)}
                                </div>
                              );
                            }}
                          </Field>
                        </Col>

                      </Row>
                      <ErrorMessage name={`value`} component="div" className="invalid-feedback" style={{ display: "block" }} />


                      <Row className="mt-3">
                        <Col>
                          <Field
                            type="textarea"
                            row="5"
                            name="notes"
                            label="Notes"
                            component={ReactstrapInput}
                            disabled={!userHasAnyPermissions(["write:settingsManagement"])} // Disable if the user doesn't have the required permission
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Button type="submit" color="primary" disabled={isSubmitting}>
                            {isUpdate ? "Update" : "Create"}
                          </Button>
                          <Button color="primary" onClick={() => cancel()} >
                            Cancel
                          </Button>
                        </Col>
                      </Row>
                    </Form>)}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Edit;
